import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { formatNumber } from "@/shared/lib/i18n/formatters.ts";
import { alertStatusLabel } from "@/machines/utils/alert-status-label.ts";
import { MapView } from "@/map";
import { Label } from "@/ui/components/ui/label";
import { Slider } from "@/ui/components/ui/slider";
import { Switch } from "@/ui/components/ui/switch";
import PolygonSelector from "@/polygon/components/polygon-selector";
import { useGetPolygonsByIdsForSelector } from "@/polygon/api/queries/polygon-selector.queries";
import { Machine } from "@/machines/types/machine";
import { useGetGps } from "@/machines/api/queries/gps.queries";
import { useGetAlerts } from "@/alerts/api/queries/machine-alerts.queries";
import { useGetTruckEvents } from "@/machines/api/queries/events.queries";
import { useDateTimeRangeStore } from "@/shared/stores/useDateTimeRangeStore";
import { useDebounce } from "use-debounce";
import { useMachineDetailMapCoordinates } from "@/machines/components/hooks/use-machine-detail-map-coordinates";
import MainPreview from "@/machines/components/common/timeline-editor/main-preview/main-preview";
import VideoPreviewEnhanced, { type VideoOrientation } from "@/machines/components/common/timeline-editor/video-preview/video-preview-enhanced";
import SecondaryCamera from "@/machines/components/common/timeline-editor/video-preview/secondary-camera";
import EmptyState from "@/shared/components/ui/empty-state/empty-state";
import { useGetVideoAround } from "@/machines/api/queries/video.queries.ts";
import { useTimelineVideoAvailability } from "@/machines/components/hooks/use-timeline-video-availability";
import { useTimelineVideoParams } from "@/machines/components/hooks/use-timeline-video-params";
import EventDetailsModal from "@/machines/components/common/event-details-modal/event-details-modal";
import MapEventOverlay from "@/machines/components/common/event-details-modal/map-event-overlay";
import { Alert, AlertStatus } from "@/alerts/types/machine-alert";
import { MachineEvent, TruckEvent } from "@/machines/types/event";
import { useTimelineContext } from "@/machines/context/timeline-context";
import { CoordinateSet as MapCoordinateSet } from "@/map";
import { useMultiCameraStore } from "@/shared/stores/useMultiCameraStore";
import { useModal } from "@/shared/lib/modal/modal-context";
import { Button } from "@/ui/components/ui/button";
import { Plus } from "lucide-react";

interface MachineDetailVideoMapTimelineSectionProps {
  machine: Machine;
  selectedTime: number | null;
  machineId?: string;
  isTimelineLoading: boolean;
  isTimelineEmpty: boolean;
}

const MachineDetailVideoMapTimelineSection = ({
  machine,
  selectedTime,
  machineId,
  isTimelineLoading,
  isTimelineEmpty,
}: MachineDetailVideoMapTimelineSectionProps) => {
  const { t } = useTranslation("machines");
  const { value } = useDateTimeRangeStore();
  const {
    setSelectedTime,
    selectedTrack,
    eventOverlayRequestTime,
    requestEventOverlay,
  } = useTimelineContext();
  const { openModal } = useModal();
  const { getCameras } = useMultiCameraStore();
  const extraCameras = getCameras(machine.id);
  const [maxPoints, setMaxPoints] = useState(100);
  const [debouncedMaxPoints] = useDebounce(maxPoints, 500);
  const [selectedPolygonIds, setSelectedPolygonIds] = useState<number[]>([]);
  const { polygons: selectedPolygons } = useGetPolygonsByIdsForSelector(selectedPolygonIds);
  const [useAlertsGps, setUseAlertsGps] = useState(false);
  const [videoOrientation, setVideoOrientation] = useState<VideoOrientation>("vertical");

  // Alert status visibility toggles
  const [visibleAlertStatuses, setVisibleAlertStatuses] = useState<Record<AlertStatus, boolean>>({
    [AlertStatus.Pending]: true,
    [AlertStatus.Resolved]: true,
    [AlertStatus.Ignored]: true,
  });

  const toggleAlertStatus = (status: AlertStatus) => {
    setVisibleAlertStatuses(prev => ({
      ...prev,
      [status]: !prev[status],
    }));
  };

  // Modal state (for full-screen modal from timeline)
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState<Alert | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<MachineEvent | null>(null);

  // Map overlay state (for overlay on map)
  const [isMapOverlayOpen, setIsMapOverlayOpen] = useState(false);
  const [mapOverlayAlert, setMapOverlayAlert] = useState<Alert | null>(null);
  const [mapOverlayTruckEvent, setMapOverlayTruckEvent] = useState<TruckEvent | null>(null);

  // Track if we're manually navigating to prevent auto-sync from interfering
  const isManualNavigationRef = useRef(false);

  const device_mxid = machine?.device_mxid ?? "";
  const locationId = machine?.location?.id ?? machine?.location_id;

  const { data: gpsList, isLoading: isGpsLoading } = useGetGps({
    device_mxid,
    end_time: value.toDate,
    start_time: value.fromDate,
    max_points: debouncedMaxPoints,
  });

  const { data: alertsList, isLoading: isAlertsLoading } = useGetAlerts({
    device_mxid,
    start_time: value.fromDate,
    end_time: value.toDate,
    page: 1,
    itemsPerPage: 1000000,
  });

  const { data: truckEventsList } = useGetTruckEvents({
    device_mxid,
    start_time: value.fromDate,
    end_time: value.toDate,
  });

  const { basicCoordinates, polygonCoordinates } =
    useMachineDetailMapCoordinates(
      machine,
      gpsList?.data,
      selectedTime as number | null,
      selectedPolygons
    );

  // Create coordinates from alerts GPS data (no clustering)
  const alertsCoordinates = useMemo<MapCoordinateSet[]>(() => {
    if (!machine || !alertsList?.data) return [];

    // Filter alerts by GPS and visible status
    const alertsWithGps = alertsList.data
      .filter(alert => alert.gps && visibleAlertStatuses[alert.status])
      .reverse();

    // Function to get color based on alert status
    const getAlertStatusColor = (status: AlertStatus): string => {
      switch (status) {
        case AlertStatus.Pending:
          return "#FFA500"; // Orange for pending
        case AlertStatus.Resolved:
          return "#00FF00"; // Green for resolved
        case AlertStatus.Ignored:
          return "#808080"; // Gray for ignored
        default:
          return "#FFFF00"; // Yellow for unknown
      }
    };

    // Map each alert directly to a coordinate (no clustering)
    return alertsWithGps.map((alert) => {
      return {
        name: `${alert.type} - ${alert.status} - ${alert.id}`,
        color: getAlertStatusColor(alert.status),
        path: [{
          lat: alert.gps!.latitude,
          lng: alert.gps!.longitude,
          alt: alert.gps!.altitude,
          time: new Date(alert.gps!.timestamp).toISOString(),
          color: getAlertStatusColor(alert.status),
          current: false,
        }],
        isActive: true,
        disableShadow: true,
        markerType: 'alert' as const,
        isReviewed: mapOverlayAlert?.id === alert.id,
      };
    });
  }, [machine, alertsList?.data, selectedTime, visibleAlertStatuses, mapOverlayAlert?.id]);

  // If alerts GPS is enabled but there are no alerts with GPS data, fall back to basic coordinates
  const hasAlertsWithGps = alertsCoordinates && alertsCoordinates.length > 0;
  const displayCoordinates = useAlertsGps
    ? basicCoordinates
    : (hasAlertsWithGps ? alertsCoordinates : basicCoordinates);
  const isMapLoading = useAlertsGps ? isGpsLoading : (isGpsLoading || isAlertsLoading);

  // Calculate timestamp and before/after parameters for video fetching
  const videoParams = useTimelineVideoParams(value.fromDate, value.toDate);

  const { data: videos } = useGetVideoAround({
    device_mxid,
    timestamp: videoParams.timestamp,
    timestamp_before: videoParams.timestamp_before,
    timestamp_after: videoParams.timestamp_after
  });

  const videoSegments = useTimelineVideoAvailability(
    videos?.data,
    value.fromDate,
    value.toDate
  );

  // Find the nearest alert to the selected time (always check, regardless of GPS mode)
  const currentAlert = useMemo(() => {
    if (!selectedTime || !alertsList?.data) return null;

    const alertsWithGps = alertsList.data.filter(alert => alert.gps);
    if (alertsWithGps.length === 0) return null;

    const selectedTimestamp = typeof selectedTime === 'number' ? selectedTime : new Date(selectedTime).getTime();

    let nearestAlert = alertsWithGps[0];
    let minDiff = Math.abs(new Date(nearestAlert.gps!.timestamp).getTime() - selectedTimestamp);

    alertsWithGps.forEach((alert) => {
      const alertTime = new Date(alert.gps!.timestamp).getTime();
      const diff = Math.abs(alertTime - selectedTimestamp);
      if (diff < minDiff) {
        minDiff = diff;
        nearestAlert = alert;
      }
    });

    // Only return the alert if it's reasonably close to the selected time (within 5 seconds)
    return minDiff < 5000 ? nearestAlert : null;
  }, [selectedTime, alertsList?.data]);

  // Find the truck event that matches the selected time
  const currentTruckEvent = useMemo(() => {
    if (!selectedTime || !truckEventsList?.data) return null;

    const selectedTimestamp = typeof selectedTime === 'number' ? selectedTime : new Date(selectedTime).getTime();

    // Find truck event where selectedTime falls within its time range
    return truckEventsList.data.find((truckEvent) => {
      const startTime = new Date(truckEvent.startTime).getTime();
      const endTime = new Date(truckEvent.endTime).getTime();
      return selectedTimestamp >= startTime && selectedTimestamp <= endTime;
    }) || null;
  }, [selectedTime, truckEventsList?.data]);

  // Automatically show map overlay when time is selected
  useEffect(() => {
    // Skip auto-sync if we're manually navigating
    if (isManualNavigationRef.current) {
      isManualNavigationRef.current = false;
      return;
    }

    if (!selectedTime) {
      // Clear overlay when no time is selected (unless something is pinned)
      if (isMapOverlayOpen && !mapOverlayAlert && !mapOverlayTruckEvent) {
        setIsMapOverlayOpen(false);
      }
      return;
    }

    // Open overlay when selectedTime is set
    setIsMapOverlayOpen(true);

    // Determine priority based on selectedTrack
    const isTruckTrackSelected = selectedTrack === 'truck_cycles' || selectedTrack === 'truck-cycle';
    const isAlertTrackSelected = selectedTrack === 'alerts';

    console.log('Auto-sync overlay:', {
      selectedTrack,
      isTruckTrackSelected,
      isAlertTrackSelected,
      currentTruckEvent: currentTruckEvent?.eventId,
      currentAlert: currentAlert?.id
    });

    // If truck track is selected, prioritize truck event
    if (isTruckTrackSelected) {
      if (currentTruckEvent) {
        console.log('Setting truck event in overlay:', currentTruckEvent.eventId);
        setMapOverlayTruckEvent(currentTruckEvent);
        setMapOverlayAlert(null);
      } else if (currentAlert) {
        setMapOverlayAlert(currentAlert);
        setMapOverlayTruckEvent(null);
      } else {
        setMapOverlayAlert(null);
        setMapOverlayTruckEvent(null);
      }
    }
    // If alert track is selected, prioritize alert
    else if (isAlertTrackSelected) {
      if (currentAlert) {
        setMapOverlayAlert(currentAlert);
        setMapOverlayTruckEvent(null);
      } else if (currentTruckEvent) {
        setMapOverlayTruckEvent(currentTruckEvent);
        setMapOverlayAlert(null);
      } else {
        setMapOverlayAlert(null);
        setMapOverlayTruckEvent(null);
      }
    }
    // Default priority: alert > truck event
    else {
      if (currentAlert) {
        setMapOverlayAlert(currentAlert);
        setMapOverlayTruckEvent(null);
      } else if (currentTruckEvent) {
        setMapOverlayTruckEvent(currentTruckEvent);
        setMapOverlayAlert(null);
      } else {
        setMapOverlayAlert(null);
        setMapOverlayTruckEvent(null);
      }
    }
  }, [selectedTime, currentAlert, currentTruckEvent, selectedTrack]);


  // Handler for when event is double-clicked on timeline (shows full modal)
  const handleEventDoubleClick = useCallback((event: MachineEvent) => {
    setSelectedEvent(event);
    setSelectedAlert(null);
    setIsModalOpen(true);
  }, []);

  // Handler for navigating to timeline from modal
  const handleNavigateToTimeline = useCallback((timestamp: number) => {
    isManualNavigationRef.current = true;
    setSelectedTime(timestamp, "external");
    // Optionally scroll to the timeline section
  }, [setSelectedTime]);

  // Handler for clicking GPS point on map
  const handleMapPointClick = useCallback((date: Date) => {
    const timestamp = new Date(date).getTime() + 100;
    setSelectedTime(timestamp + 100, "external");
  }, [setSelectedTime]);

  // Handler for closing full modal
  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    // Don't clear selected data immediately to allow smooth animation
    setTimeout(() => {
      setSelectedAlert(null);
      setSelectedEvent(null);
    }, 300);
  }, []);

  // Handler for closing map overlay
  const handleCloseMapOverlay = useCallback(() => {
    setIsMapOverlayOpen(false);
    // Don't clear selected data immediately to allow smooth animation
    setTimeout(() => {
      setMapOverlayAlert(null);
      setMapOverlayTruckEvent(null);
    }, 300);
  }, []);

  // Handler for changing to a different alert (from Prev/Next buttons or click)
  // Updates both the overlay AND seeks the timeline/video to the alert time
  const handleAlertChange = useCallback((alert: Alert) => {
    isManualNavigationRef.current = true;
    setMapOverlayAlert(alert);
    setMapOverlayTruckEvent(null);

    // Seek timeline and video to alert timestamp
    if (alert.timestamp) {
      const alertTime = new Date(alert.timestamp).getTime();
      setSelectedTime(alertTime, "internal");
    }
  }, [setSelectedTime]);

  // ─── Deep-link: surface the existing on-map event overlay ───────────
  // The ML-export "Open in machine timeline" link sets a request time. We
  // resolve the truck cycle (or nearest alert) at *that exact time* — not the
  // live cursor, which drifts via video sync — and open the existing
  // MapEventOverlay for it. No new modal is created.
  useEffect(() => {
    if (eventOverlayRequestTime == null) return;
    const ts = eventOverlayRequestTime;

    const truck =
      truckEventsList?.data?.find((te) => {
        const s = new Date(te.startTime).getTime();
        const e = new Date(te.endTime).getTime();
        return ts >= s && ts <= e;
      }) ?? null;

    const nearAlert =
      !truck && alertsList?.data
        ? alertsList.data
            .filter((a) => a.gps)
            .find(
              (a) =>
                Math.abs(new Date(a.gps!.timestamp).getTime() - ts) < 5000,
            ) ?? null
        : null;

    if (truck || nearAlert) {
      // Skip the next auto-sync so it doesn't override what we set here.
      isManualNavigationRef.current = true;
      setMapOverlayTruckEvent(truck);
      setMapOverlayAlert(nearAlert);
      setIsMapOverlayOpen(true);
    }
    // Consume the request whether or not an event was found.
    requestEventOverlay(null);
  }, [eventOverlayRequestTime, truckEventsList, alertsList, requestEventOverlay]);

  // Handler for changing to a different truck event (from Prev/Next buttons or click)
  // Updates both the overlay AND seeks the timeline/video to the event time
  const handleTruckEventChange = useCallback((truckEvent: TruckEvent) => {
    isManualNavigationRef.current = true;
    setMapOverlayTruckEvent(truckEvent);
    setMapOverlayAlert(null);

    // Seek timeline and video to truck event start time
    if (truckEvent.startTime) {
      const eventTime = new Date(truckEvent.startTime).getTime();
      setSelectedTime(eventTime, "internal");
    }
  }, [setSelectedTime]);

  return (
    <div className="grid grid-cols-12 gap-4">
      {/* Left Side - Map and Timeline */}
      <div className="flex col-span-9 flex-col gap-4">
        {/* Map Section */}
        <div className="w-full rounded-2xl p-4 bg-sidebar flex flex-col gap-4 relative">
          <div className="relative">
            <MapView
              key={`machine-detail-map-${machineId ?? ""}-${debouncedMaxPoints}-${useAlertsGps ? "alerts" : "gps"}`}
              basicCoordinates={displayCoordinates}
              polygonCoordinates={polygonCoordinates}
              options={{
                defaultZoom: 14,
                zoomControl: false,
              }}

              resizable={true}
              initialHeight={400}
              locationId={locationId}
              storageKey={"machine-detail-map-height"}
              isLoading={isMapLoading}
              emptyStateTitle={useAlertsGps ? t("detail.map.alertsEmptyTitle") : t("detail.map.gpsEmptyTitle")}
              emptyStateDescription={useAlertsGps ? t("detail.map.alertsEmptyDescription") : t("detail.map.gpsEmptyDescription")}
              loadingText={useAlertsGps ? t("detail.map.alertsLoading") : t("detail.map.gpsLoading")}
              controlsUIEnable={true}

              onClickPoint={handleMapPointClick}
              renderLeftContent={
                <div className="flex flex-col gap-2 bg-card/95 backdrop-blur-sm p-3 rounded-lg border shadow-sm">
                  {/* Polygon Selector */}
                  <div className="w-64">
                    <PolygonSelector
                      onSelectionChange={setSelectedPolygonIds}
                      selectedPolygonIds={selectedPolygonIds}
                    />
                  </div>

                  {/* GPS Source Toggle */}
                  <div className="flex items-center justify-between gap-2 py-1">
                    <Label htmlFor="gps-source-toggle" className="text-sm font-medium">
                      {t("detail.map.showAlerts", { state: useAlertsGps ? t("detail.map.off") : t("detail.map.on") })}
                    </Label>
                    <Switch
                      id="gps-source-toggle"
                      checked={!useAlertsGps}
                      onCheckedChange={(checked) => setUseAlertsGps(!checked)}
                    />
                  </div>

                  {/* Alerts Legend - Show when alerts are visible */}
                  {!useAlertsGps && alertsList?.data && alertsList.data.filter(a => a.gps).length > 0 && (
                    <div className="flex flex-col gap-1 pt-2 border-t border-border">
                      <Label className="text-xs font-medium text-muted-foreground">
                        {t("detail.map.filterByStatus")}
                      </Label>
                      <div className="flex flex-col gap-1">
                        <button
                          onClick={() => toggleAlertStatus(AlertStatus.Pending)}
                          className={`flex items-center gap-2 px-1 py-0.5 rounded hover:bg-muted/50 transition-colors ${
                            !visibleAlertStatuses[AlertStatus.Pending] ? 'opacity-40' : ''
                          }`}
                        >
                          <div
                            className="w-3 h-3 rounded-full border"
                            style={{
                              backgroundColor: visibleAlertStatuses[AlertStatus.Pending] ? "#FFA500" : "transparent",
                              borderColor: "#FFA500"
                            }}
                          />
                          <span className="text-xs">{alertStatusLabel(AlertStatus.Pending, t)}</span>
                        </button>
                        <button
                          onClick={() => toggleAlertStatus(AlertStatus.Resolved)}
                          className={`flex items-center gap-2 px-1 py-0.5 rounded hover:bg-muted/50 transition-colors ${
                            !visibleAlertStatuses[AlertStatus.Resolved] ? 'opacity-40' : ''
                          }`}
                        >
                          <div
                            className="w-3 h-3 rounded-full border"
                            style={{
                              backgroundColor: visibleAlertStatuses[AlertStatus.Resolved] ? "#00FF00" : "transparent",
                              borderColor: "#00FF00"
                            }}
                          />
                          <span className="text-xs">{alertStatusLabel(AlertStatus.Resolved, t)}</span>
                        </button>
                        <button
                          onClick={() => toggleAlertStatus(AlertStatus.Ignored)}
                          className={`flex items-center gap-2 px-1 py-0.5 rounded hover:bg-muted/50 transition-colors ${
                            !visibleAlertStatuses[AlertStatus.Ignored] ? 'opacity-40' : ''
                          }`}
                        >
                          <div
                            className="w-3 h-3 rounded-full border"
                            style={{
                              backgroundColor: visibleAlertStatuses[AlertStatus.Ignored] ? "#808080" : "transparent",
                              borderColor: "#808080"
                            }}
                          />
                          <span className="text-xs">{alertStatusLabel(AlertStatus.Ignored, t)}</span>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Number of Points Slider - only show for regular GPS */}
                  {!useAlertsGps && (
                    <div className="flex flex-col gap-2">
                      <Label htmlFor="max-points" className="text-sm font-medium">
                        {t("detail.map.numberOfPoints", { value: formatNumber(maxPoints) })}
                        {isGpsLoading && maxPoints !== debouncedMaxPoints && (
                          <span className="ml-2 text-xs text-muted-foreground">
                            {t("detail.map.updating")}
                          </span>
                        )}
                      </Label>
                      <Slider
                        id="max-points"
                        value={[maxPoints]}
                        onValueChange={(value) => setMaxPoints(value[0])}
                        min={10}
                        max={500}
                        step={10}
                        className="w-full"
                      />
                    </div>
                  )}
                </div>
              }
            />

            {/* Map Event Overlay */}
            <MapEventOverlay
              isOpen={isMapOverlayOpen}
              onClose={handleCloseMapOverlay}
              alert={mapOverlayAlert}
              truckEvent={mapOverlayTruckEvent}
              onNavigateToTimeline={handleNavigateToTimeline}
              allAlerts={alertsList?.data?.filter(a => a.gps) || []}
              allTruckEvents={truckEventsList?.data || []}
              onAlertChange={handleAlertChange}
              onTruckEventChange={handleTruckEventChange}
            />
          </div>
        </div>

        {/* Timeline Section */}
        <div className="w-full rounded-2xl p-4 bg-sidebar flex flex-col gap-4">
          <div >
            {isTimelineLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="flex items-center justify-center text-muted-foreground text-lg font-medium">
                  {t("detail.timeline.loading")}
                </div>
              </div>
            ) : isTimelineEmpty ? (
              <div className="h-full flex items-center justify-center p-4">
                <EmptyState
                  title={t("detail.timeline.emptyTitle")}
                  description={t("detail.timeline.emptyDescription")}
                  className="w-full h-full"
                />
              </div>
            ) : (
              <MainPreview
                device_mxid={device_mxid}
                videoSegments={videoSegments}
                onEventDoubleClick={handleEventDoubleClick}
              />
            )}
          </div>
        </div>
      </div>

      {/* Right Side - Video Panel */}
      <div className="col-span-3">
        <div className="w-full bg-sidebar rounded-2xl p-4 flex flex-col gap-2">
          {/* Add Another Angle */}
          {locationId && !isTimelineLoading && !isTimelineEmpty && (
            <Button
              variant="ghost"
              size="sm"
              className="w-full gap-2 text-muted-foreground"
              onClick={() =>
                openModal("add-camera-angle", {
                  machineId: machine.id,
                  locationId,
                  currentDeviceMxid: device_mxid,
                })
              }
            >
              <Plus className="h-3.5 w-3.5" />
              {t("detail.video.addAngle")}
            </Button>
          )}

          {/* Secondary Cameras on top */}
          {extraCameras.map((extraMxid) => (
            <SecondaryCamera
              key={extraMxid}
              device_mxid={extraMxid}
              machineId={machine.id}
            />
          ))}

          {/* Primary Camera */}
          <div className="w-full bg-muted/50 rounded-xl overflow-hidden"
            style={{
              aspectRatio: videoOrientation === "vertical" ? "9/16" : undefined,
              maxHeight: videoOrientation === "vertical" ? 900 : 400,
            }}
          >
            {isTimelineLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-muted-foreground text-sm font-medium">
                  {t("detail.video.loading")}
                </div>
              </div>
            ) : isTimelineEmpty ? (
              <div className="h-full flex items-center justify-center p-4">
                <EmptyState
                  title={t("detail.video.emptyTitle")}
                  description={t("detail.video.emptyDescription")}
                  className="w-full h-full"
                />
              </div>
            ) : (
              <VideoPreviewEnhanced
                device_mxid={device_mxid}
                orientation={videoOrientation}
                onOrientationChange={setVideoOrientation}
              />
            )}
          </div>
        </div>
      </div>

    </div>
  );
};

export default MachineDetailVideoMapTimelineSection;
